import { Button } from "@/components/button";
import { ChildWithProfessionals } from "@/components/child-with-professionals";
import MenuPageLayout from "@/components/layouts/MenuPageLayout";
import { SummaryList, SummaryListRow } from "@/components/summary-list";
import { Tabs, Tab } from "@/components/tabs";
import { useAppContext } from "@/contexts/app-context";
import { useCollection, useResource } from "@/hooks/resources";
import ChildType from "@/types/child-type";
import Invitation from "@/types/invitation";
import UserType from "@/types/user-type";
import { request } from "@/utils/request";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "flowbite-react";
import { capitalize } from "lodash";
import { useState, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const AccountPage = () => {
  const { t } = useTranslation(["common", "attributes"]);
  const {
    state: { selectedChildId, childAuthorizations },
  } = useAppContext();

  const [currentUser] = useResource<UserType>("/users/me");

  const [selectedChild] = useResource<ChildType>(
    `/children/${selectedChildId}`,
    {
      enabled: !!selectedChildId && selectedChildId !== "",
    },
  );

  const [isLoadingBillingPortal, setIsLoadingBillingPortal] = useState(false);

  const handleStripeBillingPortal = async () => {
    setIsLoadingBillingPortal(true);

    const response = await request.post<{ redirectUrl: string }>(
      "/stripe/billing_portal/sessions",
    );

    if (response.ok && response?.data?.redirectUrl) {
      window.location.href = response.data.redirectUrl;
    }
  };

  const [pendingInvitations, { refetch: refreshPendingInvitations }] = useCollection<Invitation>("/invitations", { refetchOnMount: true })

  return (
    <MenuPageLayout title="My Account">
      <div className="flex flex-col gap-12 w-full max-w-screen-lg">
        <Tabs>
          <Tab label="Account Info" value="account">
            <div className="p-4 flex flex-col gap-8">
              <section className="flex flex-col gap-4">
                <h2 className="text-2xl pb-4 border-b">My Account</h2>

                <SummaryList>
                  <SummaryListRow label="Name" value={currentUser?.fullName} />
                  <SummaryListRow label="Email" value={currentUser?.email} />
                  <SummaryListRow label="Phone" value={currentUser?.phoneNumber} />
                  <SummaryListRow label="Language" value={t(`language.${currentUser?.locale}`)} />
                </SummaryList>
              </section>

              <section className="flex flex-col gap-4">
                <h2 className="text-2xl pb-4 border-b">Membership</h2>

                {
                  currentUser && (
                    currentUser.activeSubscriptions.length > 0 ? (
                      <SummaryList>
                        {
                          currentUser?.activeSubscriptions.flatMap(subscription => subscription.items.map(item => (
                            <Fragment key={`subscription-item-${item.id}`}>
                              <SummaryListRow
                                label="Tier"
                                value={item.productPrice?.product?.name}
                                action={
                                  <Link
                                    to="/select-membership"
                                    className="link link-secondary hover:underline"
                                  >
                                    Change
                                  </Link>
                                }
                              />

                              <SummaryListRow
                                label={
                                  <span className="flex gap-x-1">
                                    Seats
                                    <Tooltip content="'Seats' determines the number of children accounts you can play Sing and Speak 4 Kids with.">
                                      <FontAwesomeIcon icon="info-circle" size="xs" fixedWidth className="cursor-help text-gray-700" />
                                    </Tooltip>
                                  </span>
                                }
                                value={item.quantity}
                              />

                              {subscription?.stripeSubscriptionId && (
                                <SummaryListRow
                                  label="Payment Method"
                                  value={
                                    <span
                                      dangerouslySetInnerHTML={{
                                        __html: `${capitalize(subscription.stripePaymentCardBrand)} &middot;&middot;&middot;&middot; ${subscription.stripePaymentCardLast4}`
                                      }}
                                    />
                                  }
                                  action={
                                    <Button
                                      onClick={handleStripeBillingPortal}
                                      className="link link-secondary hover:underline"
                                      loading={isLoadingBillingPortal}
                                    >
                                      Change / Cancel
                                    </Button>
                                  }
                                />
                              )}
                            </Fragment>
                          )))
                        }
                      </SummaryList>
                    ) : (
                      <p>
                        <Link to="/select-membership" className="link link-secondary">Subscribe</Link> to continue playing.
                      </p>
                    )
                  )
                }
              </section>

              <p>
                Having problems?&nbsp;
                <a
                  href="https://singandspeak4kids.com/contact-us/"
                  className="link hover:underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  Contact our support team
                </a>.
              </p>
            </div>
          </Tab>

          <Tab label="Child Info" value="child">
            <div className="p-4 flex flex-col gap-4">
              <h2 className="text-2xl pb-4 border-b">Child Information</h2>

              {
                selectedChild ? (
                  <SummaryList>
                    <SummaryListRow label="Child's Name">
                      {selectedChild.fullName} ({t(`children.gender.${selectedChild?.gender}`, { ns: "attributes" })})
                    </SummaryListRow>
                    <SummaryListRow label="Age Range" value={t(`children.ageRange.${selectedChild?.ageRange}`, { ns: "attributes" })} />
                  </SummaryList>
                ) : (
                  <div>Select a child to view their information.</div>
                )
              }
            </div>
          </Tab>

          {currentUser?.role === "parent" && (
            <Tab label="Link to Professionals" value="clients">
              <div className="p-4 flex flex-col gap-4 items-start">
                <h2 className="text-2xl pb-4 border-b w-full">My Children</h2>

                {
                  Array.from({ length: Math.max(currentUser?.totalSeats, currentUser?.requiredSeats) }, (_, i) => {
                    const childAuthorization = childAuthorizations[i]

                    return childAuthorization ? (
                      <ChildWithProfessionals key={`child-authorization-${i}`} child={childAuthorization.child} />
                    ) : (
                      <div key={`child-authorization-${i}`} className="card w-full px-4 py-3 flex gap-4 items-center justify-between">
                        <Link to="/add-child" className="link">
                          Add a new child to your account
                        </Link>
                      </div>
                    )
                  })
                }

                <h2 className="text-2xl pb-4 border-b w-full">Pending Invitations</h2>

                {
                  (pendingInvitations || []).length > 0
                    ? (pendingInvitations || []).map(invitation => {
                      const isSender = invitation.sender.id === currentUser.id

                      const handleRevoke = async () => {
                        const response = await request.put(`/invitations/${invitation.id}/revoke`)

                        if (response.ok) {
                          refreshPendingInvitations()
                        }
                      }

                      return (
                        <div key={`invitation-${invitation.id}`} className="card w-full px-4 py-3 flex gap-4 items-center justify-between">
                          <div>
                            <div>
                              {
                                isSender ? `From you to ${invitation.recipientName}` : `From ${invitation.sender.fullName} to you`
                              }
                            </div>
                            <div className="text-sm text-gray-500">Expires on {Intl.DateTimeFormat().format(new Date())}</div>
                          </div>

                          <div>
                            {
                              isSender
                                ? (
                                  <Button onClick={handleRevoke} className="text-sm link link-error">Revoke Invitation</Button>
                                )
                                : (
                                  <Link to={`/invitations/${invitation.id}`} className="link">
                                    View Invitation
                                  </Link>
                                )
                            }
                          </div>
                        </div>
                      )
                    })
                    : (
                      <p>You don&apos;t have any pending invitations <em>to</em> or <em>from</em> professionals.</p>
                    )
                }
              </div>
            </Tab>
          )}

          {currentUser?.role === "professional" && currentUser?.entitlements.includes("shared-child-authorization-access") && (
            <Tab label="Clients" value="clients">
              <div className="p-4 flex flex-col gap-4 items-start">
                <h2 className="text-2xl pb-4 border-b w-full">Clients</h2>

                {
                  Array.from({ length: currentUser?.totalSeats }, (_, i) => {
                    const childAuthorization = childAuthorizations[i]

                    return childAuthorization ? (
                      <div key={`child-authorization-${i}`} className="card w-full px-4 py-3 flex gap-4 items-center justify-between">
                        <div>
                          <div>{childAuthorization.child.fullName}</div>
                          <div className="text-sm text-gray-500">{childAuthorization.user.fullName}</div>
                        </div>

                        <div>
                          <Button className="link link-default hover:text-red-600">
                            Revoke Access
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div key={`child-authorization-${i}`} className="card w-full px-4 py-3 flex gap-4 items-center justify-between">
                        <Link to="/invite-client" className="link">
                          Invite Client
                        </Link>
                      </div>
                    )
                  })
                }

                <h2 className="text-2xl pb-4 border-b w-full">Pending Invitations</h2>

                {
                  (pendingInvitations || []).length > 0
                    ? (pendingInvitations || []).map(invitation => {
                      const isSender = invitation.sender.id === currentUser.id

                      const handleRevoke = async () => {
                        const response = await request.put(`/invitations/${invitation.id}/revoke`)

                        if (response.ok) {
                          refreshPendingInvitations()
                        }
                      }

                      return (
                        <div key={`invitation-${invitation.id}`} className="card w-full px-4 py-3 flex gap-4 items-center justify-between">
                          <div>
                            <div>
                              {
                                isSender ? `From you to ${invitation.recipientName}` : `From ${invitation.sender.fullName} to you`
                              }
                            </div>
                            <div className="text-sm text-gray-500">Expires on {Intl.DateTimeFormat().format(new Date())}</div>
                          </div>

                          <div>
                            {
                              isSender
                                ? (
                                  <Button onClick={handleRevoke} className="text-sm link link-error">Revoke Invitation</Button>
                                )
                                : (
                                  <Link to={`/invitations/${invitation.id}`} className="link">
                                    View Invitation
                                  </Link>
                                )
                            }
                          </div>
                        </div>
                      )
                    })
                    : (
                      <p>You don&apos;t have any pending invitations <em>to</em> or <em>from</em> clients.</p>
                    )
                }
              </div>
            </Tab>
          )}

          <Tab label="Support / Feedback" value="feedback">
            <div className="p-4 flex flex-col gap-2">
              <h2 className="text-2xl pb-4 border-b w-full">
                We&apos;d love to hear how things are going.
              </h2>

              <p>
                Any questions? Email us at{" "}
                <a href="mailto:SS4KidsSupport@iqsonics.com" className="link">
                  SS4KidsSupport@iqsonics.com
                </a>
              </p>

              <p>
                Please take a moment and give us your feedback. Anything from
                likes or dislikes or even a suggestion for a feature are
                welcome!
              </p>
            </div>
          </Tab>
        </Tabs>
      </div>
    </MenuPageLayout>
  );
};

export default AccountPage;
